import { createSelector } from "reselect"
import { get, chain, pickBy, mapValues, find, first } from "lodash"
import moment from "moment"
import { getAllItems, getOrderedCategoryItems, getDisplayType } from "./items"
import { getFilteredForecast, getFirst, getCurrent, CATEGORIES } from "./forecast"
import { computeCategoryItemsSubset, computeCategoryItems, computeDisplayCategories } from "helpers/analyze"

export function computeData(items, forecast) {
  return chain(forecast)
    .map((datum, index) => {
      const { date } = datum
      const result = chain(items)
        .map(item => {
          return ([
            item.id,
            item.fn
              ? item.fn(datum)
              : get(datum, item.field, 0),
          ])
        })
        .filter(([, value]) => !Number.isNaN(value))
        .fromPairs()
        .value()

      result.date = moment(date)
      result.index = index

      return result
    })
    .filter(datum => datum)
    .value()
}

export const getData = createSelector(
  [getAllItems, getFilteredForecast],
  (items, forecast) => computeData(items, forecast),
)

// We could remove the getKeys selector altogether but by keeping it and simply not filtering anything,
// we can keep using the same pattern as all the other data selectors
const getKeys = createSelector(
  [getData],
  (data) => mapValues(first(data), () => true),
)

export const getFilteredData = createSelector(
  [getData, getKeys],
  (data, keys) => data.map(item => pickBy(item, (_, key) => keys[key])),
)

export const getCategoryItemsSubset = createSelector(
  [getOrderedCategoryItems, getKeys],
  computeCategoryItemsSubset,
)

export const getCategoryItems = createSelector(
  [getOrderedCategoryItems, getKeys],
  computeCategoryItems,
)

export const getDisplayCategories = createSelector(
  [getAllItems, getKeys],
  computeDisplayCategories,
)

export const getOptions = createSelector(
  [getFirst, getCurrent, getDisplayCategories],
  (first, current, displayCategories) => {
    if (first && current) {
      return CATEGORIES.filter(category => displayCategories.indexOf(category.id) >= 0)
    } else {
      return []
    }
  },
)

export const getSelectedOption = createSelector(
  [getOptions, getDisplayType],
  (options, displayType) => {
    return get(find(options, { id: displayType }), "id")
  },
)
